import { CREDIT_ERROR_CODES } from "@/constants/credit";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { handleSupabaseFunctionError } from "./supabaseFunctionsError";
import { EntityData } from "../types";

type FormSession = Database["public"]["Tables"]["form_sessions"]["Row"];
// type EntityData = Database["public"]["Tables"]["entity_data"]["Row"];

export interface SessionWithEntity extends FormSession {
  entity_data?: EntityData;
}

/**
 * Get all form sessions for the current authenticated user
 */
export async function getUserSessions(
  options: { includeArchived?: boolean } = {}
): Promise<SessionWithEntity[]> {
  const { includeArchived = false } = options;
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return [];
    }

    let query = supabase
      .from("form_sessions")
      .select(
        `
        *,
        entity_data (
          *
        )
      `
      )
      .eq("user_id", user.id)
      .order("updated_at", { ascending: false });

    if (!includeArchived) {
      query = query.is("archived_at", null);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching user sessions:", error);
      return [];
    }

    // Process to get only the latest entity_data for each session
    const processedData = data.map((session) => {
      if (session.entity_data && Array.isArray(session.entity_data)) {
        // Get the latest entity_data by sorting by updated_at descending
        const latestEntityData = session.entity_data.sort(
          (a, b) =>
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        )[0];

        return {
          ...session,
          entity_data: latestEntityData ? [latestEntityData] : [],
        };
      }
      return session;
    });

    return processedData as unknown as SessionWithEntity[];
  } catch (error) {
    console.error("Error in getUserSessions:", error);
    return [];
  }
}

/**
 * Create a new form session for the authenticated user
 * First session is FREE, subsequent sessions cost 150 credits
 * Sessions are always created with current_step=0 for security
 */
export async function createNewUserSession(): Promise<FormSession | null> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return null;
    }

    // Call backend API to create session
    const { data, error } = await supabase.functions.invoke(
      "api/sessions/create",
      {
        method: "POST",
      }
    );

    // Handle errors
    await handleSupabaseFunctionError(error);

    // Check for failure in response (fallback for backends that return 200 with success: false)
    if (!data?.success) {
      throw new Error(data?.error || "Failed to create session");
    }

    console.log(`📝 [SESSION] Created new session: ${data.session?.id}`);

    return data.session;
  } catch (error) {
    console.error("Error in createNewUserSession:", error);
    throw error;
  }
}

/**
 * Create a new form session for unauthenticated user migration
 * This endpoint handles both session creation and credit deduction
 * - On success: Creates session at step 3 with credits deducted
 * - On insufficient credits: Creates session at step 2 without credit deduction
 */
export async function createSessionForGuest(
  isFromLandingPage: boolean,
  cui?: string
): Promise<{
  session: FormSession;
  credits_deducted: boolean;
  insufficient_credits?: boolean;
  shortage?: number;
  current_balance?: number;
  required?: number;
  new_balance?: number;
} | null> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return null;
    }

    // Call backend API to create session for guest migration.
    // CUI is passed so the server can reject the migration if an archived
    // session already exists for this user + CUI pair.
    const { data, error } = await supabase.functions.invoke(
      "api/sessions/create-for-guest",
      {
        method: "POST",
        body: { is_from_landing_page: isFromLandingPage, cui },
      }
    );

    // Handle errors
    await handleSupabaseFunctionError(error);

    // Check for failure in response
    if (!data?.success) {
      throw new Error(data?.error || "Failed to create session for guest");
    }

    console.log(
      `📝 [SESSION] Created session for guest: ${data.session?.id} at step ${data.session?.current_step}`
    );

    return {
      session: data.session,
      credits_deducted: data.credits_deducted,
      insufficient_credits: data.insufficient_credits,
      shortage: data.shortage,
      current_balance: data.current_balance,
      required: data.required,
      new_balance: data.new_balance,
    };
  } catch (error) {
    console.error("Error in createSessionForGuest:", error);
    throw error;
  }
}

/**
 * A session is "empty" when the user has not progressed past step 0 — no
 * entity data, no evaluations, no credits spent. Empty sessions can be
 * hard-deleted directly from the switcher; non-empty ones must be archived.
 */
export function isSessionEmpty(session: SessionWithEntity | FormSession): boolean {
  return session.current_step === 0;
}

/**
 * Generate a user-friendly display name for a session
 */
export function generateSessionDisplayName(
  session: SessionWithEntity,
  index: number
): string {
  // Get the first entity data (should only be one per session)
  const entityData = session.entity_data;

  // If we have entity data with CUI and company name, use that
  if (entityData?.cui && entityData?.nume_entitate) {
    return `${entityData.nume_entitate} - ${entityData.cui}`;
  }

  // If we only have CUI, use that
  if (entityData?.cui) {
    return `CUI: ${entityData.cui}`;
  }

  // If we only have company name, use that
  if (entityData?.nume_entitate) {
    return entityData.nume_entitate;
  }

  // Default to generic name with index
  return `Companie nouă`;
}

/**
 * Get session progress percentage
 */
export function getSessionProgress(session: FormSession): number {
  // Total steps in the wizard (0-4)
  const totalSteps = 5;
  // If session is completed, show 100%
  if (session.status === "completed") {
    return 100;
  }
  // Calculate progress based on current step (0-4)
  return Math.round((session.current_step / (totalSteps - 1)) * 100);
}

/**
 * Get session status display text
 */
export function getSessionStatusText(session: FormSession): string {
  switch (session.status) {
    case "completed":
      return "Finalizat";
    case "in_progress":
      return "În progres";
    default:
      return "Început";
  }
}

/**
 * Archive a session (soft-hide from the switcher without destroying data).
 */
export async function archiveSession(sessionId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from("form_sessions")
      .update({ archived_at: new Date().toISOString() })
      .eq("id", sessionId);

    if (error) {
      console.error("Error archiving session:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error in archiveSession:", error);
    return false;
  }
}

/**
 * Unarchive a session — restore it to the active set.
 */
export async function unarchiveSession(sessionId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from("form_sessions")
      .update({ archived_at: null })
      .eq("id", sessionId);

    if (error) {
      console.error("Error unarchiving session:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error in unarchiveSession:", error);
    return false;
  }
}

/**
 * Delete a session and all its related data
 */
export async function deleteSession(sessionId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from("form_sessions")
      .delete()
      .eq("id", sessionId);

    if (error) {
      console.error("Error deleting session:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error in deleteSession:", error);
    return false;
  }
}

/**
 * Duplicate a session (create a copy)
 * Costs 150 credits (unless it's the user's first session)
 */
export async function duplicateSession(
  sessionId: string
): Promise<FormSession | null> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return null;
    }

    // Get the original session data
    const { data: originalSession, error: sessionError } = await supabase
      .from("form_sessions")
      .select("*")
      .eq("id", sessionId)
      .single();

    if (sessionError || !originalSession) {
      console.error("Error fetching original session:", sessionError);
      return null;
    }

    // Create new session via API (handles credit deduction)
    const { data, error } = await supabase.functions.invoke(
      "api/sessions/create",
      {
        method: "POST",
      }
    );

    if (error) {
      console.error("Error creating duplicate session:", error);
      throw error;
    }

    if (!data.success) {
      // Handle insufficient credits error
      if (data.error === CREDIT_ERROR_CODES.INSUFFICIENT_CREDITS) {
        const insufficientError = new Error("Insufficient credits");
        (insufficientError as any).shortage = data.shortage;
        (insufficientError as any).code = "INSUFFICIENT_CREDITS";
        throw insufficientError;
      }
      throw new Error(data.error || "Failed to duplicate session");
    }

    const newSession = data.session;

    // TODO: Copy related data (entity_data, service_selections, etc.)
    // This can be implemented later if needed

    return newSession;
  } catch (error) {
    console.error("Error in duplicateSession:", error);
    throw error;
  }
}

export type CuiDuplicateMatch = {
  session: SessionWithEntity;
  isArchived: boolean;
};

/**
 * Find the user's session whose entity_data.cui matches the given CUI.
 * Active matches take precedence over archived matches.
 */
export function findDuplicateCuiSession({
  cui,
  sessions,
  archivedSessions,
  excludeSessionId,
}: {
  cui: string | null | undefined;
  sessions: SessionWithEntity[];
  archivedSessions: SessionWithEntity[];
  excludeSessionId?: string | null;
}): CuiDuplicateMatch | null {
  if (!cui) return null;

  const active = sessions.find(
    (s) => s.entity_data?.cui === cui && s.id !== excludeSessionId
  );
  if (active) return { session: active, isArchived: false };

  const archived = archivedSessions.find((s) => s.entity_data?.cui === cui);
  if (archived) return { session: archived, isArchived: true };

  return null;
}

/**
 * Find an existing session by CUI and extract preselected sectors.
 * Callers pass only active sessions, so archived ones are naturally excluded —
 * a user who archived a company should not have its sector selections
 * auto-apply to a new evaluation.
 */
export function getPreselectedSectorsFromCui({
  cui,
  sessions,
}: {
  cui: string;
  sessions: SessionWithEntity[];
}): string[] {
  const existingSession = sessions.find(
    (s) => s.entity_data?.cui === cui && s.current_step > 1
  );

  if (
    !existingSession?.entity_data?.selected_services ||
    existingSession.entity_data.selected_services.length === 0
  ) {
    return [];
  }

  return Array.from(
    new Set(
      existingSession.entity_data.selected_services.map((s) => s.sector_name)
    )
  );
}
